import { Skeleton } from '@fleet/webshop-ui';
import type { FC } from 'react';

interface SectionSkeletonProps {
  type:
    | 'passengers'
    | 'seatSelection'
    | 'ancillaries'
    | 'purchaser'
    | 'vouchers'
    | 'consents';
}

const SectionSkeleton: FC<SectionSkeletonProps> = ({ type }) => {
  switch (type) {
    case 'passengers':
      // Multiple passenger forms with fields
      return (
        <div className="flex flex-col gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-3">
              <Skeleton className="h-6 w-32" />
              <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ))}
        </div>
      );

    case 'seatSelection':
      // Seat selection UI with coach visualization
      return (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-6 w-48" />
          <div className="flex gap-4">
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      );

    case 'ancillaries':
      // Ancillary sections with accordion items
      return (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-6 w-40" />
          <div className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
      );

    case 'purchaser':
      // Fill with data buttons + purchaser form
      return (
        <div className="flex flex-col gap-3 tablet:flex-row tablet:gap-6">
          <div className="flex flex-col gap-3 tablet:flex-1">
            <Skeleton className="h-5 w-32" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      );

    case 'vouchers':
      // Add voucher input + applied vouchers list
      return (
        <div className="flex flex-col gap-3 laptop:flex-row laptop:gap-6">
          <div className="flex-1">
            <Skeleton className="mb-3 h-6 w-32" />
            <Skeleton className="mb-2 h-12 w-full" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="flex-1">
            <Skeleton className="mb-3 h-6 w-40" />
            <Skeleton className="h-5 w-full" />
          </div>
        </div>
      );

    case 'consents':
      // Consent checkboxes
      return (
        <div className="flex flex-col gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-5 w-5 shrink-0" />
              <Skeleton className="h-5 w-full" />
            </div>
          ))}
        </div>
      );

    default:
      return null;
  }
};

export default SectionSkeleton;
